import React, { useEffect, useState } from 'react';
import { Alert, Form, Input, Modal } from 'antd';

import { useAccess } from '../../../auth/AccessContext.jsx';
import { StepUpCancelled, useStepUp } from '../../../stepup/useStepUp.js';
import { REASON_MAX, reasonProblem, reasonRequest } from './orders.js';

/**
 * The checker's two other answers to a READY order, each with a reason of 3
 * to 500 characters on one line (the catalogue's rule):
 *
 *   sendBack  ORDER_SEND_BACK (IP-85, IP-127): the review cycle ends - its
 *             reviews stay in the order's history, but the order needs a new
 *             review before it can be approved. The order's figures do not
 *             change.
 *   reject    ORDER_REJECT (IP-86, IP-130): the order -> REJECTED. The other
 *             leg of a switch or transfer is rejected with it; a
 *             subscription whose money has cleared is flagged for a refund.
 *             LIV is told, with the reason as the note the investor sees.
 *             IP-199: a reject needs the checker's authenticator code
 *             (stepUp ORDER_REJECT, bound to the ids and the reason); a
 *             send-back stays on the session.
 */
const KINDS = Object.freeze({
    sendBack: {
        title: (n) => 'Send ' + (n === 1 ? 'this transaction' : n + ' transactions') + ' back for review?',
        body: 'The review cycle ends: the transaction needs a new review before it can be approved. Its reviews stay in its history, and its figures do not change.',
        ok: 'Send back',
        danger: false,
        stepUp: null,
        call: (api, orderIds, reason) => api.orders.sendBack(orderIds, reason)
    },
    reject: {
        title: (n) => 'Reject ' + (n === 1 ? 'this transaction' : n + ' transactions') + '?',
        body: 'Rejected transactions are never dealt, and LIV tells the investor your reason. The other leg of a switch or transfer is rejected with it, and a subscription whose money has cleared is flagged for a refund.',
        ok: 'Reject',
        danger: true,
        stepUp: 'ORDER_REJECT',
        call: (api, orderIds, reason, stepUp) => api.orders.reject(orderIds, reason, stepUp)
    }
});

/**
 * @param {{ kind: 'sendBack'|'reject'|null, orders: object[], onClose: () => void, onDone: (kind: string, result: object) => void }} props
 */
const ReasonModal = ({ kind, orders, onClose, onDone }) => {
    const { api } = useAccess();
    const { withStepUp, stepUpDialog } = useStepUp();
    const [form] = Form.useForm();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const spec = kind ? KINDS[kind] : null;

    // destroyOnHidden gives every opening a fresh form; only the last refusal needs clearing.
    useEffect(() => {
        if (kind) setError(null);
    }, [kind]);

    const submit = async ({ reason }) => {
        // The ids and the reason in one body the firewall takes, or why not (a long reason on many orders).
        const { body, message } = reasonRequest(kind, orders, reason);
        if (!body) {
            setError(message);
            return;
        }
        setBusy(true);
        setError(null);
        try {
            // An action that needs a code asks for it first, bound to exactly this body.
            const result = spec.stepUp
                ? await withStepUp(spec.stepUp, body, (stepUp) => spec.call(api, body.orderIds, body.reason, stepUp))
                : await spec.call(api, body.orderIds, body.reason);
            onDone(kind, result);
        } catch (e) {
            // Closing the code dialog cancels quietly: nothing was sent.
            if (e instanceof StepUpCancelled) return;
            setError(e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Modal
                open={Boolean(spec)}
                title={spec?.title(orders.length)}
                okText={spec?.ok}
                okButtonProps={{ danger: spec?.danger, loading: busy }}
                cancelButtonProps={{ disabled: busy }}
                onOk={() => form.submit()}
                onCancel={onClose}
                rootClassName="lfoc-styles-wrapper"
                destroyOnHidden
            >
                <p>{spec?.body}</p>
                {error && <Alert type="error" showIcon title={error} className="auth-alert" />}
                <Form form={form} layout="vertical" onFinish={submit} requiredMark={false}>
                    <Form.Item
                        name="reason"
                        label="Reason"
                        rules={[{ validator: (_, value) => (reasonProblem(value) ? Promise.reject(new Error(reasonProblem(value))) : Promise.resolve()) }]}
                    >
                        <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} maxLength={REASON_MAX} showCount />
                    </Form.Item>
                </Form>
            </Modal>
            {stepUpDialog}
        </>
    );
};

export default ReasonModal;
